import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { NzTableSize } from 'ng-zorro-antd/table';

import { MField, MFieldColumn, MFieldLevelOptions, MFieldsDataSource } from './data-source';
import { IMFieldColumns, IMFieldLevelToColor } from './typings';

const UNREACHABLE_ID = '_UNREACHABLE_ID';

@Component({
  selector: 'm-fields',
  template: `
    <nz-table
      class="m-fields-table"
      [nzData]="mDataSource.displayData"
      [nzVirtualItemSize]="mVirtualItemSize"
      [nzVirtualForTrackBy]="trackByIndex"
      [nzVirtualMaxBufferPx]="mVirtualMaxBufferPx"
      [nzVirtualMinBufferPx]="mVirtualMinBufferPx"
      [nzFrontPagination]="false"
      [nzHideOnSinglePage]="true"
      [nzScroll]="mScroll"
      [nzSize]="mSize"
    >
      <thead>
        <tr>
          <ng-container *ngFor="let column of internalColumns">
            <th>{{ column.name }}</th>
          </ng-container>
        </tr>
      </thead>
      <tbody>
        <ng-template nz-virtual-scroll let-item let-index="index">
          <tr>
            <ng-container *ngFor="let column of internalColumns" [ngSwitch]="column.key">
              <!-- 名称 -->
              <td *ngSwitchCase="'name'">
                <nz-space nzAlign="center">
                  <!-- 暂时隐藏选择框 -->
                  <label *ngIf="false" nz-checkbox [(ngModel)]="item.checked"></label>
                  <m-field-icon class="m-field-icon" *nzSpaceItem [mType]="item.combineType"></m-field-icon>
                  <ng-container *nzSpaceItem>
                    <ng-container *ngIf="editingId !== item.uuid; else nameEditTpl">{{ item.name }}</ng-container>
                    <ng-template #nameEditTpl>
                      <input nz-input [(ngModel)]="item.name" />
                    </ng-template>
                  </ng-container>
                </nz-space>
              </td>
              <!-- 描述 -->
              <td *ngSwitchCase="'description'">
                <ng-container *ngIf="editingId !== item.uuid; else descriptionEditTpl">{{ item.description }}</ng-container>
                <ng-template #descriptionEditTpl>
                  <input nz-input [(ngModel)]="item.description" />
                </ng-template>
              </td>
              <!-- 颜色 -->
              <td *ngSwitchCase="'color'">
                <ng-container *ngIf="editingId !== item.uuid; else colorEditTpl">
                  <input nz-input class="m-field-color" [disabled]="true" [attr.type]="'color'" [(ngModel)]="item.color" />
                </ng-container>
                <ng-template #colorEditTpl>
                  <input nz-input class="m-field-color" [attr.type]="'color'" [(ngModel)]="item.color" />
                </ng-template>
              </td>
              <!-- 类型 -->
              <td *ngSwitchCase="'combineName'">
                {{ item.combineName }}
              </td>
              <!-- level -->
              <td *ngSwitchCase="'level'">
                <nz-select
                  *ngIf="editingId === item.uuid"
                  [nzDisabled]="editingId !== item.uuid"
                  [(ngModel)]="item.level"
                  (ngModelChange)="onLevelChange(item)"
                  [nzOptions]="mLevelOptions"
                ></nz-select>
                <span *ngIf="editingId !== item.uuid">
                  {{ getLevelLabel(item.level) }}
                </span>
              </td>
              <!-- 操作 -->
              <td *ngSwitchCase="'operations'">
                <nz-space>
                  <ng-container *ngIf="editingId !== item.uuid">
                    <a *nzSpaceItem (click)="onItemEdit(item.uuid)">编辑</a>
                  </ng-container>
                  <ng-container *ngIf="editingId === item.uuid">
                    <a *nzSpaceItem (click)="onItemSave(item.uuid)">保存</a>
                  </ng-container>
                  <a *nzSpaceItem (click)="onItemDelete(item)">删除</a>
                </nz-space>
              </td>
            </ng-container>
          </tr>
        </ng-template>
      </tbody>
    </nz-table>
  `,
  host: {
    class: 'm-fields'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MFieldsComponent implements OnChanges {
  @Input() mDataSource: MFieldsDataSource = new MFieldsDataSource([]);
  @Input() mLevelOptions = MFieldLevelOptions;
  @Input() mColumns: IMFieldColumns = [];
  @Input() mLevelToColor: IMFieldLevelToColor = {
    level1: '#FA541C',
    level2: '#40a9ff',
    level3: '#73d13d'
  };
  @Input() mScroll = { y: '300px' };
  @Input() mSaveFn = (): boolean => true;
  @Input() mSize: NzTableSize = 'small';
  @Input() mVirtualItemSize = 48;
  @Input() mVirtualMaxBufferPx = 200;
  @Input() mVirtualMinBufferPx = 100;

  @Output() readonly mLevelChange = new EventEmitter<MField>();

  editingId = UNREACHABLE_ID;
  internalColumns: MFieldColumn[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    const { mColumns } = changes;

    if (mColumns) {
      this.internalColumns = this.mColumns.map(c => new MFieldColumn(c));
    }
  }

  trackByIndex(_: number, data: MField): string {
    return data.uuid;
  }

  onItemEdit(itemId: string): void {
    this.editingId = itemId;
  }

  onItemSave(itemId: string): void {
    if (!this.mSaveFn()) {
      return;
    }
    if (this.editingId === itemId) {
      this.editingId = UNREACHABLE_ID;
    }
  }

  onItemDelete(item: MField): void {
    this.mDataSource.deleteItems([item]);
  }

  onLevelChange(item: MField): void {
    this.mDataSource.updateColorByLevel(item, this.mLevelToColor);
    this.mLevelChange.emit(item);
  }

  getLevelLabel(val: string): string {
    return this.mLevelOptions.find(o => o.value === val)?.label ?? '';
  }

  render(): void {
    this.cdr.detectChanges();
  }
}
